import React, { useEffect, useState, useCallback } from "react";
import {
  Filter, Calendar, X, ChevronDown, ChevronUp, RefreshCw,
  CheckCircle2, XCircle, Clock, AlertTriangle, Loader2,
  CreditCard, Zap, Radio, ShieldCheck, ArrowRight,
  Search, Download, User, Tv2, Hash, Eye, ScanSearch,
} from "lucide-react";
import { api } from "@/lib/api";
import Pagination from "@/components/Pagination";

/* ─── Status helpers ──────────────────────────────────────────────────── */
const STATUS_MAP = {
  success:          { label: "Success",          color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2,  dot: "bg-emerald-500" },
  pending:          { label: "Pending",          color: "bg-amber-50 text-amber-700 border-amber-200",      icon: Clock,         dot: "bg-amber-400" },
  payment_verified: { label: "Payment Verified", color: "bg-blue-50 text-blue-700 border-blue-200",         icon: ShieldCheck,   dot: "bg-blue-500" },
  processing:       { label: "Processing",       color: "bg-indigo-50 text-indigo-700 border-indigo-200",   icon: Loader2,       dot: "bg-indigo-500" },
  pending_review:   { label: "Pending Review",   color: "bg-yellow-50 text-yellow-700 border-yellow-300",   icon: Eye,           dot: "bg-yellow-500" },
  recharge_failed:  { label: "Recharge Failed",  color: "bg-red-50 text-red-700 border-red-200",            icon: XCircle,       dot: "bg-red-500" },
  failed:           { label: "Payment Failed",   color: "bg-red-50 text-red-600 border-red-200",            icon: XCircle,       dot: "bg-red-400" },
  amount_mismatch:  { label: "Amount Mismatch",  color: "bg-orange-50 text-orange-700 border-orange-200",   icon: AlertTriangle, dot: "bg-orange-500" },
};
const getStatus = (s) => STATUS_MAP[s] || { label: s, color: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock, dot: "bg-slate-400" };

/* ─── Pipeline step icon ──────────────────────────────────────────────── */
const STEP_ICONS = {
  recharge_initiated:       { icon: Zap,          color: "text-blue-500" },
  razorpay_order_created:   { icon: CreditCard,   color: "text-blue-500" },
  razorpay_payment_received:{ icon: ShieldCheck,   color: "text-green-500" },
  webhook_payment_captured: { icon: Radio,         color: "text-green-600" },
  dishtv_api_request:       { icon: ArrowRight,    color: "text-indigo-500" },
  dishtv_api_success:       { icon: CheckCircle2,  color: "text-emerald-500" },
  dishtv_api_failed:        { icon: XCircle,       color: "text-red-500" },
  dishtv_api_error:         { icon: AlertTriangle, color: "text-red-600" },
  recharge_completed:       { icon: CheckCircle2,  color: "text-emerald-600" },
  recharge_failed:          { icon: XCircle,       color: "text-red-500" },
  pending_review:           { icon: Eye,           color: "text-yellow-600" },
  manual_status_update:     { icon: ShieldCheck,   color: "text-purple-500" },
  token_refresh_attempt:    { icon: RefreshCw,     color: "text-blue-500" },
  token_refreshed:          { icon: CheckCircle2,  color: "text-green-500" },
  token_refresh_failed:     { icon: XCircle,       color: "text-red-500" },
  sim_session_expired:      { icon: AlertTriangle, color: "text-amber-500" },
  webhook_payment_failed:   { icon: XCircle,       color: "text-red-500" },
  amount_mismatch:          { icon: AlertTriangle, color: "text-orange-600" },
  razorpay_manual_check:    { icon: Search,        color: "text-indigo-500" },
};
const getStepIcon = (event) => STEP_ICONS[event] || { icon: Clock, color: "text-slate-400" };

const STEP_STATUS_DOT = {
  success: "bg-emerald-500",
  error:   "bg-red-500",
  warning: "bg-amber-500",
  info:    "bg-blue-400",
};

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : '';

/* ─── Status Badge ────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = getStatus(status);
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${s.color}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </span>
  );
}

/* ─── Pipeline Timeline ───────────────────────────────────────────────── */
function PipelineTimeline({ steps }) {
  if (!steps || steps.length === 0) return (
    <p className="text-xs text-slate-400 italic py-2">No pipeline events recorded.</p>
  );
  return (
    <div className="relative pl-6">
      {/* Vertical line */}
      <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />
      {steps.map((step, i) => {
        const si = getStepIcon(step.event);
        const StepIcon = si.icon;
        const dotColor = STEP_STATUS_DOT[step.status] || "bg-slate-400";
        return (
          <div key={step.id || i} className="relative pb-4 last:pb-0">
            {/* Dot on timeline */}
            <div className={`absolute -left-6 top-1 w-[22px] h-[22px] rounded-full border-2 border-white shadow-sm flex items-center justify-center ${
              step.status === 'success' ? 'bg-emerald-50' :
              step.status === 'error' ? 'bg-red-50' : 'bg-slate-50'
            }`}>
              <StepIcon className={`w-3 h-3 ${si.color}`} />
            </div>
            {/* Content */}
            <div className="ml-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-700">
                  {step.event_label || step.event}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                <span className="text-[10px] text-slate-400 font-mono">{fmtTime(step.timestamp)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.message}</p>
              {step.amount != null && (
                <span className="text-[10px] font-mono text-slate-400 mt-0.5 inline-block">
                  Amount: {fmt(step.amount)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Expandable Row ──────────────────────────────────────────────────── */
function TransactionRow({ r, isOpen, onToggle, onStatusUpdate }) {
  const s = getStatus(r.status);
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'success' | 'recharge_failed' | null

  // ── Razorpay payment check ──
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // null | result object

  const handleCheckPayment = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await api.post(`/admin/recharges/${r.id}/check-payment`);
      setCheckResult(data);
    } catch (e) {
      setCheckResult({
        ok: false,
        message: e?.response?.data?.detail || 'Failed to check payment. Please try again.',
        payment_status: 'error',
      });
    } finally { setChecking(false); }
  };

  // ── Manual API Retry ──
  const [retrying, setRetrying] = useState(false);
  const [confirmRetry, setConfirmRetry] = useState(false);
  
  const handleRetryApi = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/admin/recharges/${r.id}/retry-api`);
      alert(data.message);
      if (onStatusUpdate) onStatusUpdate();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to retry API. Please try again.');
    } finally { setRetrying(false); }
  };
  
  // Show button for ALL Razorpay transactions (read-only payment verification)
  const canCheckPayment = r.gateway === 'razorpay' && !!r.gateway_order_id;

  const canResolve = true; // Admin has full control over any transaction status

  const handleResolve = async (newStatus) => {
    if (!reason.trim()) return alert('Please provide a reason.');
    setUpdating(true);
    try {
      await api.put(`/admin/recharges/${r.id}/status`, { status: newStatus, reason: reason.trim() });
      setReason(''); setConfirmAction(null);
      if (onStatusUpdate) onStatusUpdate();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update status.');
    } finally { setUpdating(false); }
  };

  /* Derive pipeline summary flags */
  const pipeline = r.pipeline || [];
  const hasPayment = pipeline.some(p => ['razorpay_payment_received', 'webhook_payment_captured'].includes(p.event));
  const hasDishtvCall = pipeline.some(p => p.event === 'dishtv_api_request');
  const dishtvOk = pipeline.some(p => p.event === 'dishtv_api_success');
  const dishtvFail = pipeline.some(p => ['dishtv_api_failed', 'dishtv_api_error'].includes(p.event));

  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-t border-slate-100 cursor-pointer transition-colors ${isOpen ? 'bg-indigo-50/40' : 'hover:bg-slate-50/70'}`}
      >
        {/* Txn No */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Hash className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="font-mono font-bold text-xs text-brand-primary whitespace-nowrap">
              {r.txn_no || r.id?.slice(0, 8)}
            </span>
          </div>
        </td>
        {/* Date */}
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
        {/* Customer */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-700 truncate">{r.customer_name || '—'}</p>
              <p className="text-[10px] text-slate-400 font-mono">{r.dth_id || '—'}</p>
            </div>
          </div>
        </td>
        {/* Plan */}
        <td className="px-4 py-3 text-xs text-slate-600 max-w-[120px] truncate">{r.product_name || '—'}</td>
        {/* Amount */}
        <td className="px-4 py-3 font-heading font-bold text-sm text-brand-accent whitespace-nowrap">{fmt(r.amount)}</td>
        {/* Gateway */}
        <td className="px-4 py-3">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${
            r.gateway === 'razorpay' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
          }`}>{r.gateway}</span>
        </td>
        {/* Payment */}
        <td className="px-4 py-3 text-center">
          {r.gateway === 'cash' ? (
            <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" title="Cash (no gateway)" />
          ) : hasPayment ? (
            <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" title="Payment received" />
          ) : r.status === 'failed' ? (
            <XCircle className="w-4 h-4 text-red-400 mx-auto" title="Payment failed" />
          ) : (
            <Clock className="w-4 h-4 text-amber-400 mx-auto" title="Awaiting payment" />
          )}
        </td>
        {/* DishTV */}
        <td className="px-4 py-3 text-center">
          {dishtvOk ? (
            <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" title="DishTV success" />
          ) : dishtvFail ? (
            <XCircle className="w-4 h-4 text-red-500 mx-auto" title="DishTV failed" />
          ) : hasDishtvCall ? (
            <Loader2 className="w-4 h-4 text-indigo-400 mx-auto animate-spin" title="In progress" />
          ) : (
            <span className="text-slate-300 text-xs">—</span>
          )}
        </td>
        {/* Status */}
        <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
        {/* By */}
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap capitalize">{r.initiated_by_role || '—'}</td>
        {/* Expand */}
        <td className="px-3 py-3 text-center">
          {isOpen
            ? <ChevronUp className="w-4 h-4 text-brand-primary" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </td>
      </tr>

      {/* ── Expanded detail panel ── */}
      {isOpen && (
        <tr>
          <td colSpan={11} className="bg-slate-50/80 px-4 py-0">
            <div className="py-5 grid md:grid-cols-[1fr_1fr] gap-6 max-w-5xl">
              {/* Left: Details */}
              <div className="space-y-4">
                <h5 className="text-xs font-bold uppercase tracking-widest text-slate-400">Transaction Details</h5>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 text-xs">
                  <DetailRow label="Transaction No" value={r.txn_no || '—'} mono />
                  <DetailRow label="Customer" value={`${r.customer_name || '—'} (${r.customer_mobile || ''})`} />
                  <DetailRow label="DTH ID" value={r.dth_id || '—'} mono />
                  <DetailRow label="Operator" value={r.operator || '—'} />
                  <DetailRow label="Plan" value={r.product_name || '—'} />
                  <DetailRow label="MRP" value={fmt(r.amount_mrp)} />
                  <DetailRow label="Charged" value={fmt(r.amount)} highlight />
                  {r.extra_discount > 0 && <DetailRow label="Discount" value={`-${fmt(r.extra_discount)}`} green />}
                  {r.addon_charge > 0 && <DetailRow label="Add-on" value={`+${fmt(r.addon_charge)}`} />}
                  <DetailRow label="Gateway" value={`${r.gateway} ${r.payment_mode ? `(${r.payment_mode})` : ''}`} />
                  {r.gateway_order_id && <DetailRow label="Order ID" value={r.gateway_order_id} mono />}
                  {r.gateway_txn_ref && <DetailRow label="Payment ID" value={r.gateway_txn_ref} mono />}
                  {r.recharge_api_ref && <DetailRow label="DishTV Ref" value={r.recharge_api_ref} mono />}
                  <DetailRow label="Initiated By" value={`${r.initiated_by_name || ''} (${r.initiated_by_role || ''})`} />
                  <DetailRow label="Created" value={fmtDate(r.created_at)} />
                  <DetailRow label="Updated" value={fmtDate(r.updated_at)} />
                  {r.failure_reason && <DetailRow label="Failure Reason" value={r.failure_reason} error />}
                  {r.manual_resolution && (
                    <DetailRow label="Manual Resolution" value={`${r.manual_resolution.previous_status} → ${r.manual_resolution.new_status} by ${r.manual_resolution.resolved_by_name || 'Admin'}: ${r.manual_resolution.reason}`} />
                  )}
                </div>

                {/* ── Check Payment on Razorpay ── */}
                {canCheckPayment && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                    <h5 className="text-xs font-bold uppercase tracking-widest text-indigo-700 flex items-center gap-1.5">
                      <ScanSearch className="w-3.5 h-3.5" /> Verify Payment on Razorpay
                    </h5>
                    <p className="text-[11px] text-indigo-600">
                      Customer says payment was deducted? Click below to query Razorpay directly and confirm whether the payment was actually captured or not.
                      This is a read-only check — no recharge will be triggered.
                    </p>

                    {/* Result panel */}
                    {checkResult && (
                      <div className={`rounded-lg p-3 text-xs border space-y-1.5 ${
                        checkResult.payment_status === 'captured'
                          ? 'bg-green-50 border-green-200 text-green-800'
                          : checkResult.payment_status === 'authorized'
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : checkResult.payment_status === 'failed' || checkResult.payment_status === 'error'
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-amber-50 border-amber-200 text-amber-700'
                      }`}>
                        <p className="font-bold">{checkResult.message}</p>
                        {checkResult.payment_id && (
                          <p className="font-mono text-[10px] opacity-70">Payment ID: {checkResult.payment_id}</p>
                        )}
                        {checkResult.amount_paid != null && (
                          <p className="font-mono text-[10px] opacity-70">Amount: ₹{checkResult.amount_paid}</p>
                        )}
                        {checkResult.method && (
                          <p className="font-mono text-[10px] opacity-70">Method: {checkResult.method}{checkResult.bank ? ` (${checkResult.bank})` : ''}{checkResult.vpa ? ` — ${checkResult.vpa}` : ''}{checkResult.wallet ? ` — ${checkResult.wallet}` : ''}</p>
                        )}
                        {checkResult.contact && (
                          <p className="font-mono text-[10px] opacity-70">Contact: {checkResult.contact}{checkResult.email ? ` | ${checkResult.email}` : ''}</p>
                        )}
                        {checkResult.error_description && (
                          <p className="font-mono text-[10px] opacity-70">Error: {checkResult.error_description}</p>
                        )}
                        {checkResult.total_attempts != null && (
                          <p className="font-mono text-[10px] opacity-50">Total payment attempts: {checkResult.total_attempts}</p>
                        )}
                        {checkResult.txn_status && (
                          <p className="font-mono text-[10px] opacity-50">Our txn status: {checkResult.txn_status}</p>
                        )}
                      </div>
                    )}

                    <button
                      onClick={(e) => { e.stopPropagation(); handleCheckPayment(); }}
                      disabled={checking}
                      className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2.5 rounded-lg transition"
                    >
                      {checking
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Querying Razorpay...</>
                        : <><ScanSearch className="w-3.5 h-3.5" /> Check Payment Status</>}
                    </button>
                  </div>
                )}

                {/* ── Manual API Retry ── */}
                {r.status !== 'success' && (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-3">
                    <h5 className="text-xs font-bold uppercase tracking-widest text-sky-700 flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5" /> API Operations
                    </h5>
                    <p className="text-[11px] text-sky-700">
                      If the transaction failed but money was deducted, you can manually trigger the DishTV API again without charging the user.
                    </p>
                    {!confirmRetry ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmRetry(true); }}
                        className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2.5 rounded-lg transition"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Retry DishTV Recharge API
                      </button>
                    ) : (
                      <div className="bg-white border border-sky-200 rounded-lg p-3 space-y-3">
                        <p className="text-xs font-bold text-sky-800">
                          Are you sure you want to trigger the DishTV API again?
                        </p>
                        <p className="text-[10px] text-sky-600 leading-tight">
                          This will send a fresh recharge request to DishTV using the existing transaction amount. Do this ONLY if the transaction failed but money was deducted.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRetryApi(); }}
                            disabled={retrying}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition"
                          >
                            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Yes, Retry API'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmRetry(false); }}
                            disabled={retrying}
                            className="flex-1 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Manual Resolution Panel ── */}
                {canResolve && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-3">
                    <h5 className="text-xs font-bold uppercase tracking-widest text-yellow-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Update Transaction Status
                    </h5>
                    <p className="text-[11px] text-yellow-700">
                      Verify the actual status on DishTV portal before updating. A reason is mandatory for audit trail.
                    </p>
                    <textarea
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      placeholder="Enter reason for status update (mandatory)..."
                      rows={2}
                      className="w-full text-xs border border-yellow-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400/40 resize-none"
                    />
                    {!confirmAction ? (
                      <div className="flex gap-2">
                        {r.status !== 'success' && (
                          <button
                            onClick={() => setConfirmAction('success')}
                            disabled={!reason.trim()}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Mark as Success
                          </button>
                        )}
                        {r.status !== 'recharge_failed' && (
                          <button
                            onClick={() => setConfirmAction('recharge_failed')}
                            disabled={!reason.trim()}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Mark as Failed
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-bold text-slate-700">
                          Confirm: Mark this transaction as <span className={confirmAction === 'success' ? 'text-emerald-600' : 'text-red-600'}>{confirmAction === 'success' ? 'SUCCESS' : 'FAILED'}</span>?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleResolve(confirmAction)}
                            disabled={updating}
                            className={`flex-1 text-xs font-bold text-white px-3 py-2 rounded-lg transition ${
                              confirmAction === 'success' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                            } disabled:opacity-50`}
                          >
                            {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Yes, Confirm'}
                          </button>
                          <button
                            onClick={() => setConfirmAction(null)}
                            disabled={updating}
                            className="flex-1 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right: Pipeline Timeline */}
              <div className="space-y-4">
                <h5 className="text-xs font-bold uppercase tracking-widest text-slate-400">Pipeline Activity</h5>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <PipelineTimeline steps={pipeline} />
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailRow({ label, value, mono, highlight, green, error }) {
  return (
    <div className="flex justify-between items-center px-4 py-2.5">
      <span className="text-slate-400 font-medium">{label}</span>
      <span className={`font-semibold text-right ${
        mono ? 'font-mono text-slate-600' :
        highlight ? 'text-brand-accent font-heading font-bold text-sm' :
        green ? 'text-green-600' :
        error ? 'text-red-600 max-w-[260px] text-right' :
        'text-slate-700'
      }`}>{value}</span>
    </div>
  );
}

/* ─── CSV Export ───────────────────────────────────────────────────────── */
function exportCSV(rows) {
  const header = "Txn No,Date,Customer,Mobile,DTH ID,Plan,Amount,Gateway,Status,Initiated By,Payment ID,DishTV Ref\n";
  const body = rows.map(r =>
    [
      r.txn_no || '', fmtDate(r.created_at), `"${r.customer_name || ''}"`,
      r.customer_mobile || '', r.dth_id || '', `"${r.product_name || ''}"`,
      r.amount || 0, r.gateway || '', r.status || '',
      r.initiated_by_role || '', r.gateway_txn_ref || '', r.recharge_api_ref || '',
    ].join(',')
  ).join('\n');
  const blob = new Blob([header + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `transactions_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

/* ─── Main Component ──────────────────────────────────────────────────── */
export default function Recharges() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', PAGE_SIZE);
    if (filter) params.set('status', filter);
    if (search) params.set('search', search);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    api.get(`/admin/reports/recharges?${params.toString()}`)
      .then(r => {
        setRows(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, filter, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  const handleFilterChange = (v) => { setFilter(v); setPage(1); };
  const clearDates = () => { setDateFrom(""); setDateTo(""); setPage(1); };
  const hasDateFilter = dateFrom || dateTo;

  const handleDateFrom = (v) => { setDateFrom(v); setPage(1); };
  const handleDateTo = (v) => { setDateTo(v); setPage(1); };

  const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const successCount = rows.filter(r => r.status === "success").length;
  const failedCount = rows.filter(r => ['failed', 'recharge_failed'].includes(r.status)).length;
  const reviewCount = rows.filter(r => r.status === 'pending_review').length;

  if (loading && rows.length === 0) return (
    <div className="flex items-center justify-center py-20 text-slate-400">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading transactions…
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Transaction Report</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Track every recharge with full pipeline visibility.
            {total > 0 && <span className="ml-1 font-semibold text-slate-500">({total} total)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(rows)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg transition">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover px-3 py-2 rounded-lg transition">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Page" value={rows.length} sub={`of ${total} total`} color="text-slate-700" bg="bg-white" />
        <SummaryCard label="Success" value={successCount} sub={`${fmt(totalAmount)} page total`} color="text-emerald-700" bg="bg-emerald-50" />
        <SummaryCard label="Review" value={reviewCount} sub="needs attention" color="text-yellow-700" bg="bg-yellow-50" />
        <SummaryCard label="Failed" value={failedCount} sub="confirmed failed" color="text-red-700" bg="bg-red-50" />
        <SummaryCard label="Volume" value={fmt(totalAmount)} sub="page total" color="text-brand-primary" bg="bg-indigo-50" isAmount />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        {/* Search */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Txn no, customer, DTH ID, mobile..."
              className="w-full pl-8 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary/20" />
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Status</label>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select value={filter} onChange={e => handleFilterChange(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-2 text-slate-600 bg-white">
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="payment_verified">Payment Verified</option>
              <option value="processing">Processing</option>
              <option value="pending_review">Pending Review</option>
              <option value="recharge_failed">Recharge Failed</option>
              <option value="failed">Payment Failed</option>
              <option value="amount_mismatch">Amount Mismatch</option>
            </select>
          </div>
        </div>

        {/* Date From */}
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">From</label>
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <input type="date" value={dateFrom} onChange={e => handleDateFrom(e.target.value)}
              max={dateTo || undefined}
              className="text-xs border border-slate-200 rounded-lg px-2 py-2 text-slate-600 bg-white" />
          </div>
        </div>

        {/* Date To */}
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">To</label>
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <input type="date" value={dateTo} onChange={e => handleDateTo(e.target.value)}
              min={dateFrom || undefined}
              className="text-xs border border-slate-200 rounded-lg px-2 py-2 text-slate-600 bg-white" />
          </div>
        </div>

        {hasDateFilter && (
          <button onClick={clearDates}
            className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 px-2 py-2 rounded-md hover:bg-red-50 transition">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      {rows.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          <Hash className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No transactions found</p>
          <p className="text-xs mt-1">Adjust your filters or date range.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {loading && (
            <div className="flex items-center justify-center py-2 text-xs text-slate-400 border-b border-slate-100">
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Loading...
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
              <tr>
                <th className="text-left px-4 py-3">Txn No</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Gateway</th>
                <th className="text-center px-4 py-3">Payment</th>
                <th className="text-center px-4 py-3">DishTV</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">By</th>
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <TransactionRow
                  key={r.id}
                  r={r}
                  isOpen={openRow === r.id}
                  onToggle={() => setOpenRow(openRow === r.id ? null : r.id)}
                  onStatusUpdate={load}
                />
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} total={total} />
        </div>
      )}
    </div>
  );
}

/* ─── Summary Card ────────────────────────────────────────────────────── */
function SummaryCard({ label, value, sub, color, bg, isAmount }) {
  return (
    <div className={`rounded-xl border border-slate-200 px-4 py-3 ${bg}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`font-heading font-black ${isAmount ? 'text-lg' : 'text-2xl'} ${color} mt-0.5`}>{value}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}
